/** @jsxImportSource @emotion/react */
import { Button } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { confirm } from 'components/molecules/Confirmation'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import {
  useConfirmOrderBillingTypeMutation,
  useUnlockOrderClientBillingMutation,
} from 'store/services/labOrder'
import { Order } from 'store/types'

interface IProps {
  order?: Order
}

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { message?: string } })?.data?.message || fallback

/**
 * The client-bill state of an order, above its insurance panel.
 *
 * - Review pending (Mixed office): an alert asking the biller to decide
 *   Client Bill vs Insurance. Client Bill puts the office's client-bill payer
 *   in as the locked primary and the order is marked billed on its own.
 * - Client-billed: a lock banner explaining why the insurance cannot be
 *   edited, with the way out (payer-edit rights) beside it.
 * - Decided as Insurance: a quiet banner keeping the other answer reachable —
 *   a mistaken "Bill as Insurance" must not be final.
 */
export const ClientBillingPanel = ({ order }: IProps) => {
  const canEditInsurancePayers = useLabPermission(UserPermission.EDIT_INSURANCE_PAYERS)
  const [confirmBillingType, { isLoading: isConfirming }] = useConfirmOrderBillingTypeMutation()
  const [unlockClientBilling, { isLoading: isUnlocking }] = useUnlockOrderClientBillingMutation()

  if (!order) {
    return null
  }

  const decide = async (billingType: 'INSURANCE' | 'CLIENT_BILL') => {
    if (billingType === 'CLIENT_BILL') {
      try {
        await confirm('', {
          title: 'Bill this order to the client?',
          description:
            "The office's client bill payer becomes the primary payer, any insurance entered is removed," +
            ' the insurance panel is locked and the order is marked billed. Switching back later needs' +
            ' payer-edit rights.',
          yesAction: 'Bill as Client Bill',
        })
      } catch {
        return
      }
    }

    try {
      await confirmBillingType({ orderId: Number(order.id), billingType }).unwrap()
      toast.success(
        billingType === 'CLIENT_BILL' ? 'Order will be billed to the client' : 'Order will be billed to insurance',
      )
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to set the billing type'))
    }
  }

  const unlock = async () => {
    try {
      await confirm('', {
        title: 'Switch this order to insurance billing?',
        description:
          'The insurance panel is unlocked and the automatic billing is taken back — the order returns to' +
          ' Billing Review to be billed for real. The client bill payer stays until you replace it.',
        yesAction: 'Switch to Insurance',
      })
    } catch {
      return
    }

    try {
      await unlockClientBilling({ orderId: Number(order.id) }).unwrap()
      toast.success('Order switched to insurance billing')
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to switch the order to insurance billing'))
    }
  }

  if (order.billingTypeReview) {
    return (
      <div
        className="py-2 px-4 w-100 mb-3"
        css={{ background: '#fff3cd', border: '1px solid #ffe69c', borderRadius: '5px' }}>
        <div className="text-bold mb-1">Client Bill or Insurance?</div>
        <div css={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          This office bills some orders to the client and some to insurance. Confirm how this order is
          billed before it goes any further.
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          className="mr-2"
          disabled={isConfirming}
          onClick={() => decide('INSURANCE')}>
          Bill as Insurance
        </Button>
        <Button variant="primary" size="sm" disabled={isConfirming} onClick={() => decide('CLIENT_BILL')}>
          Bill as Client Bill
        </Button>
      </div>
    )
  }

  if (order.billingType === 'CLIENT_BILL') {
    return (
      <div
        className="py-2 px-4 w-100 mb-3 d-flex align-items-center justify-content-between"
        css={{ background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '5px' }}>
        <div>
          <div className="text-bold">
            <i className="fas fa-lock mr-2" />
            Client bill — insurance is locked
          </div>
          <div css={{ fontSize: '0.875rem' }}>
            The client is invoiced for this order. Its primary payer is the office&apos;s client bill payer
            and cannot be changed; the order is marked billed automatically.
          </div>
        </div>
        {canEditInsurancePayers && (
          <Button variant="outline-secondary" size="sm" disabled={isUnlocking} onClick={unlock}>
            Switch to Insurance
          </Button>
        )}
      </div>
    )
  }

  // The decision stamp only exists on Mixed-office orders, so an INSURANCE
  // stamp means the biller answered the review — keep the other answer
  // reachable in case Insurance was picked by mistake.
  if (order.billingType === 'INSURANCE') {
    return (
      <div
        className="py-2 px-4 w-100 mb-3 d-flex align-items-center justify-content-between"
        css={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '5px' }}>
        <div css={{ fontSize: '0.875rem' }}>
          <i className="fas fa-file-invoice-dollar mr-2" />
          Billed to insurance. This office also bills orders to the client — the choice can still be changed.
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={isConfirming}
          onClick={() => decide('CLIENT_BILL')}>
          Bill as Client Bill
        </Button>
      </div>
    )
  }

  return null
}
